import { useRef, useState } from "react"
import { ConfigProvider, DatePicker, DatePickerProps } from "antd"
import dayjs, { Dayjs } from "dayjs"
import styled from "styled-components"

import { UITypes } from "types"

import { getLocalizedDatePickerLocale } from "utils/datePickerLocale"

import SelectTimeContainer, { TimeIndicator } from "./SelectTimeContainer"
import { timeSelectOptions } from "./timeSelectOptions"

const Container = styled.div`
  display: flex;
  flex-direction: column-reverse;

  .ant-picker-outlined {
    opacity: 0;
    display: none;
  }

  .ant-picker {
    position: relative;
    height: 32px;
    padding-bottom: 3px;
    z-index: 10000;
  }

  .ant-picker-footer {
    display: none;
  }

  .ant-picker-dropdown {
    position: relative !important;
    inset: 0 auto auto 0 !important;
  }

  .ant-picker-panel-container {
    box-shadow: none !important;
  }

  .ant-picker-body {
    padding: 6px !important;
  }
`

interface DateWithSelectTimePickerProps {
  onDateTimeChange: (dateTime: Dayjs) => void
}

const DateWithSelectTimePicker = ({ onDateTimeChange }: DateWithSelectTimePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null)
  const [selectedTime, setSelectedTime] = useState<UITypes.Option[]>([timeSelectOptions[0]])
  const [timeIndicator, setTimeIndicator] = useState<TimeIndicator>("AM")
  const container = useRef<HTMLDivElement>(null)

  const handleTimeIndicatorClick = (indicator: TimeIndicator) => {
    setTimeIndicator(indicator)
    calculateDate(selectedDate, selectedTime, indicator)
  }

  const handleChangeOption = (option: UITypes.Option[]) => {
    setSelectedTime(option)
    calculateDate(selectedDate, option, timeIndicator)
  }

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date)
    calculateDate(date, selectedTime, timeIndicator)
  }

  const calculateDate = (date: Dayjs | null, selectedTime: UITypes.Option[], indicator: TimeIndicator) => {
    if (!date) {
      return
    }

    const time = (selectedTime[0].value as string).split(":")
    const hour = parseInt(time[0] === "12" ? "0" : time[0])
    const minute = parseInt(time[1])

    const newDate = date.hour(indicator === "AM" ? hour : hour + 12).minute(minute)
    onDateTimeChange(newDate)
  }

  const disabledRange: DatePickerProps["disabledDate"] = current => {
    return current < dayjs().startOf("day")
  }

  return (
    <Container ref={container}>
      <ConfigProvider
        theme={{
          token: {
            motion: false,
            colorPrimary: "#43e0f8",
            colorPrimaryBg: "#ebfcff",
          },
        }}
      >
        <DatePicker
          open
          placement="topLeft"
          locale={getLocalizedDatePickerLocale()}
          getPopupContainer={() => container.current!}
          onChange={handleDateChange}
          disabledDate={disabledRange}
        />
      </ConfigProvider>
      <SelectTimeContainer
        selectedTime={selectedTime}
        timeIndicator={timeIndicator}
        onChangeOption={handleChangeOption}
        onTimeIndicatorClick={handleTimeIndicatorClick}
      />
    </Container>
  )
}

export default DateWithSelectTimePicker
